import { useState, useEffect, useCallback } from 'react';
import { post } from '../helpers/apiHelper';

/**
 * Hook personalizado para manejar el motor de puntos automático
 * Soporta tanto PUNTOS (con límites) como ACUMULACION (sin límites)
 * 
 * @param {Object} ticket - Objeto del ticket con puntos actuales
 * @param {Object} marca - Objeto de la marca con pointsRules
 * @param {String} tipoFidelizacion - 'PUNTOS' o 'ACUMULACION'
 * @param {String} ticketId - ID del ticket para llamadas al API
 * @param {Array} productosDisponibles - Array completo de productos disponibles (opcional)
 * @returns {Object} Objeto con estados y funciones del motor
 */
export const usePointsEngine = (ticket, marca, tipoFidelizacion, ticketId, productosDisponibles = []) => {
    const [motorActivo, setMotorActivo] = useState(false);
    const [puntosDisponibles, setPuntosDisponibles] = useState(Infinity);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Detectar si el motor está activo
    useEffect(() => {
        const activo = !!(marca?.pointsEngineVersion === 1 && marca?.pointsRules);
        setMotorActivo(activo);

        // Calcular puntos disponibles según tipo de fidelización
        if (tipoFidelizacion === 'PUNTOS' && marca?.puntosTicket) {
            const puntosActuales = ticket?.puntos || 0;
            const limiteTicket = marca.puntosTicket;
            const disponibles = Math.max(0, limiteTicket - puntosActuales);
            setPuntosDisponibles(disponibles);
        } else {
            // ACUMULACION no tiene límite
            setPuntosDisponibles(Infinity);
        }
    }, [marca, ticket, tipoFidelizacion]);

    /**
     * Calcula puntos localmente (fallback)
     */
    const calcularPuntosLocal = useCallback((amount, rules, puntosDisponiblesLimit = Infinity) => {
        if (!rules || amount <= 0) return { puntos: 0, puntosOriginales: 0, puntosAjustados: 0, puntosPerdidos: 0 };

        const { mode, ratePerAmount, tiers, multiplierByTag, rounding } = rules;
        let puntos = 0;

        switch (mode) {
            case 'RATE_PER_AMOUNT':
                const { rate, step } = ratePerAmount || {};
                if (rate && step) {
                    puntos = Math.floor(amount / step) * rate;
                }
                break;

            case 'TIERED':
                if (tiers && tiers.length > 0) {
                    const tier = tiers.find(t => amount >= t.min && (!t.max || amount <= t.max));
                    if (tier) {
                        puntos = Math.floor(amount * tier.rate);
                    }
                }
                break;

            case 'MULTIPLIER_BY_TAG':
                if (multiplierByTag?.baseRate) {
                    puntos = Math.floor(amount * multiplierByTag.baseRate);
                }
                break;

            default:
                puntos = 0;
        }

        // Aplicar redondeo
        switch (rounding) {
            case 'FLOOR':
                puntos = Math.floor(puntos);
                break;
            case 'CEIL':
                puntos = Math.ceil(puntos);
                break;
            case 'ROUND':
                puntos = Math.round(puntos);
                break;
            default:
            // NONE - no hacer nada
        }

        puntos = Math.max(puntos, 0);
        const puntosOriginales = puntos;

        // Aplicar límite si es PUNTOS
        let puntosAjustados = puntos;
        let puntosPerdidos = 0;

        if (puntosDisponiblesLimit !== Infinity && puntos > puntosDisponiblesLimit) {
            puntosAjustados = puntosDisponiblesLimit;
            puntosPerdidos = puntos - puntosDisponiblesLimit;
        }

        return {
            puntos: puntosAjustados,
            puntosOriginales,
            puntosAjustados,
            puntosPerdidos
        };
    }, []);

    /**
     * Calcula preview de puntos llamando al endpoint del backend
     * Si falla, usa cálculo local como fallback
     */
    const calcularPreview = useCallback(async (monto, productos = []) => {
        if (!motorActivo || !monto || !ticketId) {
            setPreview(null);
            return null;
        }

        setLoading(true);
        setError(null);

        try {
            // Intentar llamar al endpoint del backend
            // productos puede ser un array de objetos con productId y cantidad (selectedProducts)
            const items = Array.isArray(productos) && productos.length > 0
                ? productos.map(p => {
                    // Si tiene productId, buscar el producto completo en el array de productos disponibles
                    let producto = p;
                    if (typeof p === 'object' && p.productId && Array.isArray(productosDisponibles)) {
                        producto = productosDisponibles.find(prod => prod._id === p.productId) || p;
                    }

                    return {
                        name: producto?.nombre || producto?.name || 'Producto',
                        price: producto?.precio || producto?.price || 0,
                        quantity: p?.cantidad || producto?.quantity || 1,
                        tags: producto?.tags || []
                    };
                })
                : [];

            const response = await post(`tickets/${ticketId}/points/calculate`, {
                amount: monto,
                items: items.length > 0 ? items : [],
                date: new Date().toISOString()
            });

            // Procesar respuesta del backend
            const puntosCalculados = response.points || 0;
            const calculationDetail = response.calculationDetail || {};
            const capsApplied = calculationDetail.capsApplied || [];

            // Encontrar límite aplicado
            const limiteTicket = capsApplied.find(
                cap => cap.type === 'TICKET_LIMIT' || cap.type === 'MAX_POINTS_PER_TXN_OR_TICKET_LIMIT'
            );

            const previewData = {
                points: puntosCalculados,
                calculationDetail: {
                    ...calculationDetail,
                    capsApplied
                },
                metadata: {
                    ...response.metadata,
                    puntosDisponibles: puntosDisponibles,
                    limiteAplicado: !!limiteTicket,
                    puntosPerdidos: limiteTicket?.puntosPerdidos || 0
                }
            };

            setPreview(previewData);
            return previewData;
        } catch (error) {
            console.warn('Error llamando a /points/calculate, usando cálculo local:', error);

            // Fallback a cálculo local
            const calculoLocal = calcularPuntosLocal(monto, marca.pointsRules, puntosDisponibles);

            const previewData = {
                points: calculoLocal.puntos,
                calculationDetail: {
                    basePoints: calculoLocal.puntosOriginales,
                    capsApplied: calculoLocal.puntosPerdidos > 0 ? [{
                        type: 'TICKET_LIMIT',
                        value: marca.puntosTicket,
                        puntosDisponibles: puntosDisponibles,
                        puntosPerdidos: calculoLocal.puntosPerdidos
                    }] : [],
                    finalPoints: calculoLocal.puntosAjustados
                },
                metadata: {
                    engineVersion: 1,
                    mode: marca.pointsRules.mode,
                    puntosDisponibles: puntosDisponibles,
                    limiteAplicado: calculoLocal.puntosPerdidos > 0,
                    puntosPerdidos: calculoLocal.puntosPerdidos,
                    fallback: true // Indicar que se usó cálculo local
                }
            };

            setPreview(previewData);
            setError('Se usó cálculo local (endpoint no disponible)');
            return previewData;
        } finally {
            setLoading(false);
        }
    }, [motorActivo, ticketId, marca, puntosDisponibles, calcularPuntosLocal, productosDisponibles]);

    /**
     * Verifica si hay límite aplicado en el preview
     */
    const tieneLimiteAplicado = useCallback(() => {
        if (!preview) return false;
        return preview.metadata?.limiteAplicado || false;
    }, [preview]);

    /**
     * Obtiene los puntos perdidos del preview
     */
    const puntosPerdidos = useCallback(() => {
        if (!preview) return 0;
        return preview.metadata?.puntosPerdidos || 0;
    }, [preview]);

    /**
     * Verifica si el ticket está completo
     */
    const ticketCompleto = useCallback(() => {
        if (tipoFidelizacion !== 'PUNTOS') return false;
        const puntosActuales = ticket?.puntos || 0;
        const limiteTicket = marca?.puntosTicket || 0;
        return puntosActuales >= limiteTicket;
    }, [ticket, marca, tipoFidelizacion]);

    return {
        motorActivo,
        puntosDisponibles,
        preview,
        loading,
        error,
        calcularPreview,
        tieneLimiteAplicado,
        puntosPerdidos,
        ticketCompleto,
        calcularPuntosLocal // Exportar para uso directo si es necesario
    };
};

